import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { LoadingButton } from "@mui/lab";
import { Box, Button } from "@mui/material";
import { Contact, ContactType, useGetContactLazyQuery } from "@repo/server-api";
import { RuleFactoryType, TabKeys } from "@repo/server-api/types";
import { useNotification, RuleComponent } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { JSX, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { validErrorState } from "@repo/legacy/components/helper/Index";
import { useUpdateCompanyContact } from "@repo/legacy/components/hooks/ContactMutationHooks";
import ContractedCompanies from "@repo/legacy/components/shared/additionalInformation/ContractedCompanies";
import PaymentInformation from "@repo/legacy/components/shared/additionalInformation/PaymentInformation";
import Departments from "@repo/legacy/components/shared/sections/Departments";
import RollingNumbers from "@repo/legacy/components/shared/sections/RollingNumbers";
import { BccRecipientLists } from "@repo/shared-email/components";
import usePersistentState from "@repo/legacy/store/usePersistentState";

import BillingInformation from "../../components/shared/additionalInformation/BillingInformation";
import GeneralInfo from "../../components/shared/projects/GeneralInfoContact";
import CompanyMembers from "../../components/shared/sections/CompanyMembersSection";
import ContactPersons from "../../components/shared/sections/ContactPersons";
import Tabs from "../../components/shared/Tabs";
import Dunnings from "@repo/legacy/components/shared/sections/Dunnings";

const Contractor = (props: { ruleFactory: RuleFactoryType }) => {
	const { t, i18n } = useTranslation();

	const navigate = useNavigate();
	const [contact, setContact] = usePersistentState("contractor", null);
	const [tabIndex, setTabIndex] = useState<number>(0);

	const { showNotification } = useNotification();

	const [updateCompanyContact] = useUpdateCompanyContact();

	const { id } = useParams<{ id: string }>();

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<any>({});

	const generalInfoRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const paymentInformationRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);
	const contractedRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	}>(null);

	const [tabs, setTabs] = useState<{ label: string; key: TabKeys; content: JSX.Element }[]>([]);

	const [getContact] = useGetContactLazyQuery({
		variables: { id: id ?? "" },
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.node) {
				setContact(data.node as Contact);
			}
		},
	});

	useEffect(() => {
		if (id !== contact?.id) getContact();
	}, [id]);

	const validate = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		switch (tabs[tabIndex].key) {
			case TabKeys.GENERAL_INFORMATION:
				localErrors.generalInfo = (await generalInfoRef.current?.validate()) || {};
				break;
			// TODO - Do we need this here?
			case TabKeys.PAYMENT_INFORMATION:
				localErrors.paymentInformation = (await paymentInformationRef.current?.validate()) || {};
				break;
			// ------------------------------
			default:
				break;
		}

		setErrors(localErrors);

		return validErrorState(localErrors);
	};

	const handleSubmit = async () => {
		const isValid = await validate();
		if (isValid) {
			setSaveClicked(true);
			handleSave();
		}
	};

	const handleSave = () => {
		updateCompanyContact(contact)
			.then((res) => {
				if (typeof res === "string") {
					showNotification(t("contractors.contractorUpdated"), "success");
				} else {
					showNotification(res.message, "error");
				}
				setSaveClicked(false);
			})
			.catch((err) => {
				showNotification(err.message, "error");
				setSaveClicked(false);
			});
	};

	const handleCancel = () => {
		setSaveClicked(false);
		navigate(-1);
		setContact(null);
	};

	const allTabs = useMemo(
		() => [
			{
				label: t("tab.general-information"),
				key: TabKeys.GENERAL_INFORMATION,
				content: (
					<GeneralInfo
						ref={generalInfoRef}
						errors={errors.generalInfo}
						setErrors={(value) =>
							setErrors({
								...errors,
								generalInfo: { ...errors.generalInfo, ...value },
							})
						}
						contact={contact}
						setContact={setContact}
						ruleFactory={props.ruleFactory}
					/>
				),
			},
			{
				label: t("tab.contracted-companies"),
				key: TabKeys.CONTRACTED,
				content: (
					<ContractedCompanies
						ref={contractedRef}
						errors={errors.contracted}
						setErrors={(value) =>
							setErrors({
								...errors,
								contracted: { ...errors.contracted, ...value },
							})
						}
						contact={contact}
						setContact={setContact}
					/>
				),
			},
			{
				label: t("tab.contact-persons"),
				key: TabKeys.CONTACT_PERSONS,
				content: (
					<ContactPersons
						contact={contact}
						setContact={setContact}
						ruleFactory={props.ruleFactory}
					/>
				),
			},
			{
				label: t("tab.employees"),
				key: TabKeys.MEMBERS,
				content: (
					<CompanyMembers
						contact={contact}
						setContact={setContact}
						ruleFactory={props.ruleFactory}
						contactType={ContactType.Employee}
					/>
				),
			},
			{
				label: t("tab.billing-information"),
				key: TabKeys.BILLING_INFORMATION,
				content: (
					<BillingInformation
						contact={contact}
						setContact={setContact}
						ruleFactory={props.ruleFactory}
					/>
				),
			},
			{
				label: t("contacts.payment"),
				key: TabKeys.PAYMENT_INFORMATION,
				content: (
					<PaymentInformation
						ref={paymentInformationRef}
						errors={errors.paymentInformation}
						setErrors={(value) =>
							setErrors({
								...errors,
								paymentInformation: { ...errors.paymentInformation, ...value },
							})
						}
						contact={contact}
						setContact={setContact}
					/>
				),
			},
			{
				label: t("tab.rolling-numbers"),
				key: TabKeys.ROLLING_NUMBERS,
				content: <RollingNumbers contact={contact} getContact={getContact} />,
			},
			{
				label: t("tab.bcc-lists"),
				key: TabKeys.BCC_LISTS,
				content: <BccRecipientLists contact={contact} getContact={getContact} />,
			},
			{
				label: t("departments"),
				key: TabKeys.DEPARTMENTS,
				content: <Departments contact={contact} />,
			},
			{
				label: t("dunnings"),
				key: TabKeys.DUNNINGS,
				content: <Dunnings contact={contact} />,
			},
		],

		[t, contact, errors],
	);

	useEffect(() => {
		setTabs([
			...allTabs.filter((tab) =>
				props.ruleFactory.allowedTabsForContact(contact).includes(tab.key),
			),
		]);
	}, [errors, contact, t, i18n.language, allTabs]);

	return (
		<Box
			style={{
				minHeight: "90vh",
				width: "100%",
				display: "flex",
				flexDirection: "column",
			}}>
			<Box>
				<Button className="left" variant="outlined" onClick={handleCancel}>
					<ArrowBackIcon />
				</Button>
				<div color="text.primary" className="left purpleHeader" style={{ marginLeft: 20 }}>
					{contact?.displayName}
					<span className="lightGreyFont" style={{ marginLeft: 10, fontSize: "12px" }}>
						({contact?.displayContactType.map((x) => t(x.toLowerCase())).join(", ")})
					</span>
				</div>
			</Box>
			<Box sx={{ flex: 1 }}>
				<Tabs
					tabs={tabs}
					tabIndex={tabIndex}
					setTabIndex={setTabIndex}
					rulefactory={props.ruleFactory}
				/>
			</Box>
			{tabs[tabIndex] && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", py: 2, pr: "30px" }}>
					<RuleComponent rule={props.ruleFactory.generalSaveButton(tabs[tabIndex].key)}>
						<LoadingButton
							loading={saveClicked}
							className={saveClicked ? "" : "blueButton"}
							color="secondary"
							variant="contained"
							onClick={handleSubmit}>
							{t("save")}
						</LoadingButton>
					</RuleComponent>
				</Box>
			)}
		</Box>
	);
};

export default Contractor;
